import React, { useState } from "react";
import { Table, DatePicker, Space, TableColumnsType, Button } from "antd";
import classnames from "classnames";
import { AiOutlineLeftCircle } from "react-icons/ai";
import { Link } from "react-router-dom";

import { DateFormat } from "../../../../constants";
import { Header } from "../../../../components/header";

import styles from "./ITStatistic.module.scss";
import { useDeleteAccountRequests } from "../../info-technology-process/delete-user-account/hooks";
import { useEPRCodeRequests } from "../../info-technology-process/epr-process/hooks";
import { useCodeProcessRequests } from "../../info-technology-process/code-process/hooks";
import { useRepairToolsRequests } from "../../info-technology-process/repair-tools/hooks";
import { useDemandForSuppliesRequests } from "../../info-technology-process/demand-for-supplies/hooks";

import { requestStatusTypes } from "../../info-technology-process/types";
import { countStatus, filterRequestsByRangeDate } from "../../utils";
import { FontFa } from "../../../../components";
import { useITDepartmentRequests } from "../hooks";
import { downloadExcel } from "react-export-table-to-excel";
import { DateHelper } from "../../../../helper";
import { getLabelStatus } from "../../info-technology-process/delete-user-account/utils";

const { RangePicker } = DatePicker;

enum ITDepartment {
  AccountDeleteGranted = "account-delete-granted",
  SabreEPRGranted = "sabre-epr-granted",
  ITProgramGranted = "it-program-granted",
  RepairToolGranted = "repair-tool-granted",
  DemandForSupplies = "demand-for-supplies",
}

const ITStatisticPage = () => {
  const [rangeDate, setRangeDate] = useState(null);

  const { mappedDeleteAccountRequests } = useDeleteAccountRequests();
  const { mappedEPRCodeRequests } = useEPRCodeRequests();
  const { mappedCodeProcessRequests } = useCodeProcessRequests();
  const { mappedRepairToolsRequests } = useRepairToolsRequests();
  const { mappedDemandForSuppliesRequests } = useDemandForSuppliesRequests();
  const { allITDepartmentRequests } = useITDepartmentRequests();

  // Filter by createdDateTime
  const filteredDeleteAccountByCreatedDateTime = filterRequestsByRangeDate(mappedDeleteAccountRequests, rangeDate);
  const filteredCodeProcessByCreatedDateTime = filterRequestsByRangeDate(mappedCodeProcessRequests, rangeDate);
  const filteredEPRCodeByCreatedDateTime = filterRequestsByRangeDate(mappedEPRCodeRequests, rangeDate);
  const filteredRepairToolsByCreatedDateTime = filterRequestsByRangeDate(mappedRepairToolsRequests, rangeDate);
  const filteredDemandForSuppliesByCreatedDateTime = filterRequestsByRangeDate(
    mappedDemandForSuppliesRequests,
    rangeDate
  );

  const filteredAllITDepartmentRequests = filterRequestsByRangeDate(allITDepartmentRequests, rangeDate);

  // Mapping
  const deleteAccountStatusCounts = countStatus(filteredDeleteAccountByCreatedDateTime);
  const codeProcessStatusCounts = countStatus(filteredCodeProcessByCreatedDateTime);
  const eprCodeStatusCounts = countStatus(filteredEPRCodeByCreatedDateTime);
  const repairToolsStatusCounts = countStatus(filteredRepairToolsByCreatedDateTime);
  const demandForSuppliesStatusCounts = countStatus(filteredDemandForSuppliesByCreatedDateTime);

  const totalVotes =
    deleteAccountStatusCounts.total +
    eprCodeStatusCounts.total +
    codeProcessStatusCounts.total +
    repairToolsStatusCounts.total +
    demandForSuppliesStatusCounts.total;

  const columns: TableColumnsType<any> = [
    {
      title: "Tên quy trình",
      dataIndex: "processName",
      key: "processName",
      align: "left" as const,
    },
    {
      title: "Số phiếu",
      dataIndex: "total",
      key: "total",
      align: "center" as const,
      render: (text: string) => <span className={styles.totalText}>{text}</span>,
    },

    {
      title: "Đang đợi thực thi",
      dataIndex: requestStatusTypes.submittedAssignee,
      key: requestStatusTypes.submittedAssignee,
      align: "center" as const,
      render: (text: string, record: any) => (
        <Link
          style={{ textDecoration: "none" }}
          to={`/procedure/${record.key}?status=${requestStatusTypes.submittedAssignee}`}
        >
          {text || 0}
        </Link>
      ),
    },
    {
      title: "Đang đợi duyệt",
      dataIndex: requestStatusTypes.submitted,
      key: requestStatusTypes.submitted,
      align: "center" as const,
      render: (text: string, record: any) => (
        <Link style={{ textDecoration: "none" }} to={`/procedure/${record.key}?status=${requestStatusTypes.submitted}`}>
          {text || 0}
        </Link>
      ),
    },
    {
      title: "Đã duyệt",
      dataIndex: requestStatusTypes.approved,
      key: requestStatusTypes.approved,
      align: "center" as const,
      render: (text: string, record: any) => (
        <Link style={{ textDecoration: "none" }} to={`/procedure/${record.key}?status=${requestStatusTypes.approved}`}>
          {text || 0}
        </Link>
      ),
    },
    {
      title: "Đang thực hiện",
      dataIndex: requestStatusTypes.inProgress,
      key: requestStatusTypes.inProgress,
      align: "center" as const,
      render: (text: string, record: any) => (
        <Link
          style={{ textDecoration: "none" }}
          to={`/procedure/${record.key}?status=${requestStatusTypes.inProgress}`}
        >
          {text || 0}
        </Link>
      ),
    },
    {
      title: "Đã tiếp nhận",
      dataIndex: requestStatusTypes.acceptance,
      key: requestStatusTypes.acceptance,
      align: "center" as const,
      render: (text: string, record: any) => (
        <Link
          style={{ textDecoration: "none" }}
          to={`/procedure/${record.key}?status=${requestStatusTypes.acceptance}`}
        >
          {text || 0}
        </Link>
      ),
    },
    {
      title: "Đã hoàn tất",
      dataIndex: requestStatusTypes.completed,
      key: requestStatusTypes.completed,
      align: "center" as const,
      render: (text: string, record: any) => (
        <Link style={{ textDecoration: "none" }} to={`/procedure/${record.key}?status=${requestStatusTypes.completed}`}>
          {text || 0}
        </Link>
      ),
    },
    {
      title: "Đã từ chối",
      dataIndex: requestStatusTypes.rejected,
      key: requestStatusTypes.rejected,
      align: "center" as const,
      render: (text: string, record: any) => (
        <Link style={{ textDecoration: "none" }} to={`/procedure/${record.key}?status=${requestStatusTypes.rejected}`}>
          {text || 0}
        </Link>
      ),
    },
  ];

  const dataSource = [
    {
      key: ITDepartment.SabreEPRGranted,
      processName: "Cấp code Amadeus ",
      ...eprCodeStatusCounts,
    },
    {
      key: ITDepartment.ITProgramGranted,
      processName: "Cấp quyền sử dụng chương trình",
      ...codeProcessStatusCounts,
    },
    {
      key: ITDepartment.RepairToolGranted,
      processName: "Sửa chửa CNTT",
      ...repairToolsStatusCounts,
    },
    {
      key: ITDepartment.AccountDeleteGranted,
      processName: "Xoá tài khoản",
      ...deleteAccountStatusCounts,
    },
    {
      key: ITDepartment.DemandForSupplies,
      processName: "Nhu cầu cấp vật tư, CCDC CNTT",
      ...demandForSuppliesStatusCounts,
    },
  ];

  const exportExcel = () => {
    const header = [
      "Số phiếu",
      "Dịch vụ CNTT",
      "Ngày giờ tạo",
      "Ngày giờ hoàn thành",
      "Trạng thái",
      "Người tạo phiếu",
      "Cơ quan/đơn vị",
      "Chi tiết đề nghị",
      "Lý do",
    ];

    const body: any = filteredAllITDepartmentRequests.map((e: any) => {
      const fields = {
        status: e.fields?.eRequestStatus,
        requestCompletedLabel: "hoàn thành",
      };
      return {
        id: e.id,
        title: e.fields?.Title,
        createdDateTime: DateHelper.formatDateToString(e.createdDateTime, { format: DateFormat.DD_MM_YYYY_HH_MM }),
        completeDateTime: DateHelper.formatDateToString(e.fields?.eAssigneeStatusDate, {
          format: DateFormat.DD_MM_YYYY_HH_MM,
        }),
        status: getLabelStatus(fields),
        createdBy: e.createdBy?.user.email,
        dept: e.fields?.eDepartment,
        details: e.fields?.eDescription,
      };
    });
    downloadExcel({
      fileName: "ThongKeBaoCaoCNTT",
      sheet: "Sheet 1",
      tablePayload: {
        header,
        body,
      },
    });
  };

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.wrapper}>
        <Space size="small">
          <Link to="/procedure/statistics">
            <AiOutlineLeftCircle size={38} color="gray" />
          </Link>
          <h1 className={styles.title}>Thống kê phòng CNTT</h1>
        </Space>

        <div className={styles.selectRangeDateWrapper}>
          <div>
            <p className={styles.selectRangeDateLabel}>Chọn ngày</p>
            <RangePicker
              value={rangeDate}
              onChange={(dates: any) => setRangeDate(dates)}
              placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
              format={DateFormat.DD_MM_YYY}
            />
          </div>
        </div>
        <Table
          bordered
          pagination={false}
          dataSource={dataSource}
          columns={columns}
          title={() => (
            <div className="flex d-flex justify-content-between">
              <div className={styles.tableHeader}>BP. CNTT: {totalVotes} phiếu</div>
              <Button
                type="primary"
                title="Xuất báo cáo"
                icon={<FontFa name="faFileExport" />}
                onClick={() => exportExcel()}
              >
                Xuất báo cáo
              </Button>
            </div>
          )}
          summary={(pageData) => {
            if (!pageData.length) return null;

            const totalVotes = pageData.reduce((total, current) => (total += current.total), 0);
            const totalSubmittedAssignee = pageData.reduce(
              (total, current) => (total += current[requestStatusTypes.submittedAssignee] || 0),
              0
            );
            const totalSubmitted = pageData.reduce(
              (total, current) => (total += current[requestStatusTypes.submitted] || 0),
              0
            );
            const totalApproved = pageData.reduce(
              (total, current) => (total += current[requestStatusTypes.approved] || 0),
              0
            );
            const totalInProgress = pageData.reduce(
              (total, current) => (total += current[requestStatusTypes.inProgress] || 0),
              0
            );
            const totalAcceptance = pageData.reduce(
              (total, current) => (total += current[requestStatusTypes.acceptance] || 0),
              0
            );
            const totalCompleted = pageData.reduce(
              (total, current) => (total += current[requestStatusTypes.completed] || 0),
              0
            );
            const totalRejected = pageData.reduce(
              (total, current) => (total += current[requestStatusTypes.rejected] || 0),
              0
            );

            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <div className={styles.totalLabel}>Tổng cộng</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalVotes}</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalSubmittedAssignee}</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalSubmitted}</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalApproved}</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalInProgress}</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalAcceptance}</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalCompleted}</div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <div className={classnames("flex-center", styles.totalText)}>{totalRejected}</div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </div>
    </div>
  );
};

export default ITStatisticPage;
